import type { PlaybackLayer } from 'components/Playback/types';
import {
  resolveSurveyPlaybackSelection,
  type PlaybackWfsLayerSelection,
  type SurveyPlaybackPanelLayer,
} from './surveyPlaybackHelpers';

export type PlaySurveyPanelLayer = SurveyPlaybackPanelLayer;

export type PlaySurveyClickDecision =
  | { readonly outcome: 'stop-playback' }
  | { readonly outcome: 'survey-not-found' }
  | { readonly outcome: 'no-wfs-layers' }
  | {
      readonly outcome: 'capacity-limit';
      readonly selection: PlaybackWfsLayerSelection;
    }
  | {
      readonly outcome: 'load-and-start';
      readonly selection: PlaybackWfsLayerSelection;
    };


export function resolvePlaySurveyClick(input: {
  readonly surveySlug: string;
  readonly activeSurveySlug: string | null;
  readonly panelLayers: ReadonlyArray<PlaySurveyPanelLayer> | null;
  readonly maxSimultaneousImages: number;
}): PlaySurveyClickDecision {
  if (input.activeSurveySlug === input.surveySlug) {
    return { outcome: 'stop-playback' };
  }
  if (input.panelLayers == null) {
    return { outcome: 'survey-not-found' };
  }

  const selectionDecision = resolveSurveyPlaybackSelection(
    input.panelLayers,
    input.maxSimultaneousImages,
  );
  if (selectionDecision.outcome === 'no-wfs-layers') {
    return { outcome: 'no-wfs-layers' };
  }
  if (selectionDecision.outcome === 'capacity-limit') {
    return {
      outcome: 'capacity-limit',
      selection: selectionDecision.selection,
    };
  }
  return {
    outcome: 'load-and-start',
    selection: selectionDecision.selection,
  };
}

export type PlaySurveyPlaybackDecision =
  | { readonly outcome: 'stop-playback' }
  | { readonly outcome: 'survey-not-found' }
  | { readonly outcome: 'no-wfs-layers' }
  | {
      readonly outcome: 'capacity-limit';
      readonly selection: PlaybackWfsLayerSelection;
    }
  | { readonly outcome: 'no-playback-features' }
  | {
      readonly outcome: 'start-playback';
      readonly selection: PlaybackWfsLayerSelection;
      readonly orderedLayers: readonly PlaybackLayer[];
    };

/**
 * End-to-end Play button -> playback decision
 *
 * Order:
 * 1. Same survey already playing -> stop
 * 2. Survey must exist in the panel
 * 3. WFS selection (+ capacity only for explicit over-check)
 * 4. Require loaded playback features
 * 5. Start playback with the selected (capped) layers
 */
export function resolvePlaySurveyPlayback(input: {
  readonly surveySlug: string;
  readonly activeSurveySlug: string | null;
  readonly panelLayers: ReadonlyArray<PlaySurveyPanelLayer> | null;
  readonly maxSimultaneousImages: number;
  readonly loadedPlaybackLayers: readonly PlaybackLayer[];
}): PlaySurveyPlaybackDecision {
  const clickDecision = resolvePlaySurveyClick({
    surveySlug: input.surveySlug,
    activeSurveySlug: input.activeSurveySlug,
    panelLayers: input.panelLayers,
    maxSimultaneousImages: input.maxSimultaneousImages,
  });

  if (clickDecision.outcome === 'stop-playback') {
    return { outcome: 'stop-playback' };
  }
  if (clickDecision.outcome === 'survey-not-found') {
    return { outcome: 'survey-not-found' };
  }
  if (clickDecision.outcome === 'no-wfs-layers') {
    return { outcome: 'no-wfs-layers' };
  }
  if (clickDecision.outcome === 'capacity-limit') {
    return {
      outcome: 'capacity-limit',
      selection: clickDecision.selection,
    };
  }

  if (input.loadedPlaybackLayers.length === 0) {
    return { outcome: 'no-playback-features' };
  }

  //session start also rejects over-capacity layer sets
  if (input.loadedPlaybackLayers.length > input.maxSimultaneousImages) {
    return {
      outcome: 'capacity-limit',
      selection: clickDecision.selection,
    };
  }

  return {
    outcome: 'start-playback',
    selection: clickDecision.selection,
    orderedLayers: input.loadedPlaybackLayers,
  };
}
